/**
 * CURRENCY
 *
 * The wallet is denominated in USD (profile `coins` are USD cents). Everything a user
 * sees in another currency is a DISPLAY conversion on top of that.
 *
 * Rates come from GET currency/rates, which returns per-1-USD rates:
 *   { base: "USD", markup_percent: 0.65, rates: [{ currency_code, symbol, rate, buy, sell }] }
 *
 *   rate === buy  -> the mid/interbank rate. Used for DISPLAY equivalents.
 *   sell          -> rate + markup_percent. What is actually charged on a conversion.
 *
 * Display uses `buy` deliberately: showing a balance marked up by the spread would
 * overstate what the money is worth. Anything TRANSACTIONAL should quote the server via
 * POST currency/convert, which returns `final_amount` with the markup applied - never
 * this module's arithmetic.
 */

const RATES_KEY = 'amejpay.rates';
const COUNTRIES_KEY = 'amejpay.currencyCountries';
const DISPLAY_KEY = 'amejpay.displayCurrency';

/** Rates are refreshed daily by the backend, so a day is the natural lifetime. */
export const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export const BASE_CURRENCY = 'USD';

function readCache(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) { return null; }
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.at) { return null; }
    return parsed;
  } catch (e) { return null; }
}

function writeCache(key, payload) {
  try {
    window.localStorage.setItem(key, JSON.stringify({ at: Date.now(), payload: payload }));
  } catch (e) { /* private mode - falls back to fetching each time */ }
}

export function isFresh(entry, ttl) {
  if (!entry || !entry.at) { return false; }
  return (Date.now() - entry.at) < (ttl || CACHE_TTL_MS);
}

/* ------------------------------------------------------------------ *
 * Rates
 * ------------------------------------------------------------------ */

export function cacheRates(payload) {
  if (payload && Array.isArray(payload.rates)) { writeCache(RATES_KEY, payload); }
  return payload;
}

/** Cached rates if still inside the 24h window, otherwise null. */
export function getCachedRates() {
  const entry = readCache(RATES_KEY);
  return isFresh(entry) ? entry.payload : null;
}

/** Cached rates regardless of age - better a stale rate than a blank screen. */
export function getAnyCachedRates() {
  const entry = readCache(RATES_KEY);
  return entry ? entry.payload : null;
}

export function ratesAgeMs() {
  const entry = readCache(RATES_KEY);
  return entry ? (Date.now() - entry.at) : null;
}

export function clearRatesCache() {
  try {
    window.localStorage.removeItem(RATES_KEY);
    window.localStorage.removeItem(COUNTRIES_KEY);
  } catch (e) { /* noop */ }
}

function ratesOf(source) {
  if (!source) { return []; }
  if (Array.isArray(source)) { return source; }
  return Array.isArray(source.rates) ? source.rates : [];
}

/** The rate row for a currency code, or null. */
export function findRate(source, code) {
  const list = ratesOf(source);
  const wanted = String(code || '').toUpperCase();
  for (let i = 0; i < list.length; i++) {
    if (String(list[i].currency_code || '').toUpperCase() === wanted) { return list[i]; }
  }
  return null;
}

export function isSupported(source, code) {
  return Boolean(findRate(source, code));
}

/** Currency codes we hold a rate for, USD first then alphabetical. */
export function supportedCurrencies(source) {
  const list = ratesOf(source).slice();
  list.sort(function (a, b) {
    const ac = String(a.currency_code || ''), bc = String(b.currency_code || '');
    if (ac === BASE_CURRENCY) { return -1; }
    if (bc === BASE_CURRENCY) { return 1; }
    return ac.localeCompare(bc);
  });
  return list;
}

export function symbolOf(source, code) {
  const row = findRate(source, code);
  return (row && row.symbol) || '';
}

export function currencyNameOf(source, code) {
  const row = findRate(source, code);
  return (row && row.currency_name) || String(code || '');
}

/**
 * Convert between any two currencies in the table.
 * Rates are per 1 USD, so anything not starting from USD is normalised through it.
 * `useSell` applies the marked-up rate - only for previewing a real conversion.
 */
export function convert(source, amount, from, to, useSell) {
  const value = Number(amount);
  if (isNaN(value)) { return null; }

  const fromCode = String(from || BASE_CURRENCY).toUpperCase();
  const toCode = String(to || BASE_CURRENCY).toUpperCase();
  if (fromCode === toCode) { return value; }

  const key = useSell ? 'sell' : 'buy';
  const pick = function (row) {
    if (!row) { return null; }
    const r = row[key] !== undefined && row[key] !== null ? row[key] : row.rate;
    const n = Number(r);
    return isNaN(n) ? null : n;
  };

  const fromRate = fromCode === BASE_CURRENCY ? 1 : pick(findRate(source, fromCode));
  const toRate = toCode === BASE_CURRENCY ? 1 : pick(findRate(source, toCode));
  if (fromRate === null || toRate === null || !fromRate) { return null; }

  return (value / fromRate) * toRate;
}

/** Zero-decimal currencies would look wrong with cents. */
const ZERO_DECIMAL = ['JPY', 'KRW', 'IDR', 'VND'];

export function decimalsFor(code) {
  return ZERO_DECIMAL.indexOf(String(code || '').toUpperCase()) !== -1 ? 0 : 2;
}

/**
 * How many decimals an EXCHANGE RATE needs, which is not the same as the currency's
 * own precision. JPY has no cents, but "1 USD = ¥ 159" throws away real accuracy, and
 * "1 USD = € 0.87" hides the difference between 0.866 and 0.874. Small rates get more
 * places; large ones need fewer.
 */
export function rateDecimalsFor(rate) {
  const n = Math.abs(Number(rate) || 0);
  if (n === 0) { return 2; }
  if (n < 1) { return 4; }
  if (n < 100) { return 2; }
  return 2;
}

/** "1 USD = ¥ 159.43" */
export function formatRate(source, rate, code) {
  return formatCurrency(source, rate, code, { decimals: rateDecimalsFor(rate) });
}

/** "रू 152,689.22" - symbol when we have one, code otherwise. */
export function formatCurrency(source, amount, code, options) {
  const opts = options || {};
  const n = Number(amount);
  if (isNaN(n)) { return ''; }
  const currency = String(code || BASE_CURRENCY).toUpperCase();
  const decimals = opts.decimals !== undefined ? opts.decimals : decimalsFor(currency);

  let text;
  try {
    text = n.toLocaleString(undefined, {
      minimumFractionDigits: decimals, maximumFractionDigits: decimals
    });
  } catch (e) {
    text = n.toFixed(decimals);
  }

  if (opts.codeOnly) { return currency + ' ' + text; }
  const symbol = symbolOf(source, currency);
  return symbol ? (symbol + ' ' + text) : (currency + ' ' + text);
}

/* ------------------------------------------------------------------ *
 * Countries -> display currency
 * ------------------------------------------------------------------ */

export function cacheCountries(payload) {
  const list = (payload && payload.countries) || payload;
  if (Array.isArray(list)) { writeCache(COUNTRIES_KEY, list); }
  return list;
}

export function getCachedCountries() {
  const entry = readCache(COUNTRIES_KEY);
  return isFresh(entry) ? entry.payload : null;
}

export function getAnyCachedCountries() {
  const entry = readCache(COUNTRIES_KEY);
  return entry ? entry.payload : null;
}

/**
 * The currency a country should be shown in.
 *
 * `display_currency` is the field to use, NOT `currency_code`: the API already falls
 * back to USD for currencies it holds no rate for. Afghanistan's currency_code is AFN
 * but its display_currency is USD, and converting into a rate we do not have would
 * produce a blank or a wrong number.
 */
export function currencyForCountry(countries, countryNameOrCode) {
  const list = Array.isArray(countries) ? countries : ((countries && countries.countries) || []);
  const wanted = String(countryNameOrCode || '').trim().toLowerCase();
  if (!wanted) { return BASE_CURRENCY; }
  for (let i = 0; i < list.length; i++) {
    const c = list[i] || {};
    if (String(c.name || '').toLowerCase() === wanted ||
        String(c.code || '').toLowerCase() === wanted) {
      return c.display_currency || c.currency_code || BASE_CURRENCY;
    }
  }
  return BASE_CURRENCY;
}

/* ------------------------------------------------------------------ *
 * The currency the person is currently viewing in
 * ------------------------------------------------------------------ */

/**
 * The currency the person has EXPLICITLY chosen, or "" if they never have.
 *
 * Use this - not getDisplayCurrency("") - to ask "did they pick one?". Passing an
 * empty fallback to getDisplayCurrency cannot answer that: `fallback || BASE_CURRENCY`
 * turns "" into "USD", so the answer was always yes and defaults derived from the
 * account or the country of business never got applied.
 */
export function getStoredDisplayCurrency() {
  try {
    const saved = window.localStorage.getItem(DISPLAY_KEY);
    return saved ? String(saved) : '';
  } catch (e) { return ''; }
}

export function hasDisplayCurrency() {
  return Boolean(getStoredDisplayCurrency());
}

export function getDisplayCurrency(fallback) {
  const saved = getStoredDisplayCurrency();
  if (saved) { return saved; }
  return fallback || BASE_CURRENCY;
}

export function setDisplayCurrency(code) {
  try {
    if (code) { window.localStorage.setItem(DISPLAY_KEY, String(code).toUpperCase()); }
    else { window.localStorage.removeItem(DISPLAY_KEY); }
  } catch (e) { /* noop */ }
  return code;
}

export default {
  BASE_CURRENCY, CACHE_TTL_MS, isFresh,
  cacheRates, getCachedRates, getAnyCachedRates, ratesAgeMs, clearRatesCache,
  findRate, isSupported, supportedCurrencies, symbolOf, currencyNameOf,
  convert, decimalsFor, rateDecimalsFor, formatRate, formatCurrency,
  cacheCountries, getCachedCountries, getAnyCachedCountries, currencyForCountry,
  getDisplayCurrency, setDisplayCurrency, getStoredDisplayCurrency, hasDisplayCurrency
};
